// ─────────────────────────────────────────────────────────────────────────────
// AddExternalReferenceComponent
// ─────────────────────────────────────────────────────────────────────────────
// Dialog for creating and editing product external references.
// Uses <app-shared-dialog> + mat-autocomplete for product/customer search.
// Customer is required (unlike aliases where it was optional).
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedDialogComponent } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { ProductsService } from 'src/app/features/produits/services/produits.services';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { IExternalReference, ICreateExternalReferencePayload, IUpdateExternalReferencePayload } from '../../models/external-reference.models';
import {
  CreateExternalReference,
  CreateExternalReferenceSuccess,
  UpdateExternalReference,
  UpdateExternalReferenceSuccess,
} from '../../store/external-references.actions';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';

interface SelectOption {
  value: number;
  label: string;
  sku?: string;
}

@Component({
  selector: 'app-add-external-reference',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, SharedModule, UiModule, SharedDialogComponent],
  templateUrl: './add-external-reference.component.html',
  styleUrls: ['./add-external-reference.component.scss'],
})
export class AddExternalReferenceComponent implements OnInit, OnDestroy {
  // ── Form ──────────────────────────────────────────────────────────────────

  form!: FormGroup;
  productSearchControl = new FormControl('');
  customerSearchControl = new FormControl('');

  // ── State ─────────────────────────────────────────────────────────────────

  isUpdate = false;
  canSubmit = false;
  canSubmit$: Observable<boolean> = of(false);

  // ── Autocomplete results ──────────────────────────────────────────────────

  filteredProducts$!: Observable<SelectOption[]>;
  filteredCustomers$!: Observable<SelectOption[]>;
  productSearchState: 'idle' | 'searching' | 'results' = 'idle';
  customerSearchState: 'idle' | 'searching' | 'results' = 'idle';

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AddExternalReferenceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IExternalReference | null,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private actions$: Actions,
    private translateService: TranslateService,
    private productsService: ProductsService,
    private customersService: CustomersService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.initForm();
    this.initProductAutocomplete();
    this.initCustomerAutocomplete();
    this.initClearDetection();
    this.handleEditMode();

    this.canSubmit$ = this.store.select(selectCanDo('knowledge_base', this.isUpdate ? 'edit' : 'create'));
    this.canSubmit$.pipe(takeUntil(this.destroy$)).subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
      if (canSubmit) {
        this.form.enable({ emitEvent: false });
        this.productSearchControl.enable({ emitEvent: false });
        this.customerSearchControl.enable({ emitEvent: false });
      } else {
        this.form.disable({ emitEvent: false });
        this.productSearchControl.disable({ emitEvent: false });
        this.customerSearchControl.disable({ emitEvent: false });
      }
    });

    this.listenForSuccess();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Initialization ────────────────────────────────────────────────────────

  private initForm(): void {
    this.form = this.fb.group({
      sku: ['', Validators.required],
      reference_code: ['', Validators.required],
      customer_id: [null, Validators.required],
    });
  }

  private initProductAutocomplete(): void {
    this.filteredProducts$ = this.productSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const keyword = typeof term === 'string' ? term : '';
        if (!keyword || keyword.length < 2) {
          this.productSearchState = 'idle';
          return of([]);
        }
        this.productSearchState = 'searching';
        return this.productsService.fetchProductsBySearch({ number: 1, size: 20 }, { filters: [] }, keyword, undefined, false, true).pipe(
          map((res) => {
            const options = (res.data ?? [])
              .filter((p) => p.id != null && p.sku)
              .map((p) => ({
                value: p.id!,
                label: `${p.name} (${p.sku})`,
                sku: p.sku,
              }));
            this.productSearchState = 'results';
            return options;
          }),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  private initCustomerAutocomplete(): void {
    this.filteredCustomers$ = this.customerSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const keyword = typeof term === 'string' ? term : '';
        if (!keyword || keyword.length < 2) {
          this.customerSearchState = 'idle';
          return of([]);
        }
        this.customerSearchState = 'searching';
        return this.customersService.listCustomers({ number: 1, size: 20 }, undefined, keyword).pipe(
          map((res) => {
            const options = (res.data ?? []).map((c) => ({
              value: c.id,
              label: c.name,
            }));
            this.customerSearchState = 'results';
            return options;
          }),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  private initClearDetection(): void {
    this.productSearchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (typeof value === 'string' && !value) {
        this.form.patchValue({ sku: '' });
      }
    });

    this.customerSearchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (typeof value === 'string' && !value) {
        this.form.patchValue({ customer_id: null });
      }
    });
  }

  private handleEditMode(): void {
    if (!this.data) return;

    this.isUpdate = true;

    this.form.patchValue({
      sku: this.data.sku,
      reference_code: this.data.reference_code,
      customer_id: this.data.customer_id,
    });

    // Set display text for product autocomplete
    if (this.data.product_name) {
      const label = `${this.data.product_name}${this.data.sku ? ' (' + this.data.sku + ')' : ''}`;
      this.productSearchControl.setValue({ value: this.data.product_id, label, sku: this.data.sku } as any);
    }
    // Set display text for customer autocomplete
    if (this.data.customer_name) {
      this.customerSearchControl.setValue({ value: this.data.customer_id, label: this.data.customer_name } as any);
    }
  }

  private listenForSuccess(): void {
    this.actions$
      .pipe(ofType(CreateExternalReferenceSuccess, UpdateExternalReferenceSuccess), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.close());
  }

  // ── Autocomplete handlers ─────────────────────────────────────────────────

  onProductSelected(option: SelectOption): void {
    this.form.patchValue({ sku: option.sku });
  }

  onCustomerSelected(option: SelectOption): void {
    this.form.patchValue({ customer_id: option.value });
  }

  clearProduct(): void {
    this.productSearchControl.setValue('');
    this.form.patchValue({ sku: '' });
  }

  clearCustomer(): void {
    this.customerSearchControl.setValue('');
    this.form.patchValue({ customer_id: null });
  }

  displayProductFn = (option: SelectOption | string): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label ?? '';
  };

  displayCustomerFn = (option: SelectOption | string): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label ?? '';
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  submitPermission() {
    return { module: 'knowledge_base', action: this.isUpdate ? 'edit' : 'create' };
  }

  save(): void {
    if (!this.canSubmit) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { sku, reference_code, customer_id } = this.form.getRawValue();

    if (this.isUpdate && this.data) {
      const payload: IUpdateExternalReferencePayload = {
        sku,
        reference_code,
        customer_id,
      };
      this.store.dispatch(UpdateExternalReference({ id: this.data.id, payload }));
    } else {
      const payload: ICreateExternalReferencePayload = {
        sku,
        reference_code,
        customer_id,
      };
      this.store.dispatch(CreateExternalReference({ payload }));
    }
  }

  // ── Close ─────────────────────────────────────────────────────────────────

  close(): void {
    this.dialogRef.close();
  }
}
